import jsPDF from 'jspdf';
import { utils, writeFileXLSX } from 'xlsx';
import { prepararLogoPdf, desenharLogoPdf } from './logoPdf';

// Tamanho do logo no cabeçalho, em milímetros.
const LOGO_MM = { largura: 35, altura: 12 };

const normalizeColumns = (cols = []) =>
  cols.map(c =>
    typeof c === 'string' ? { key: c, label: c } : { key: c.key, label: c.label }
  );

/**
 * Sentinel: Sanitizes a value to prevent CSV/Formula Injection.
 * If a string starts with =, +, -, or @, it is prepended with a single quote.
 * @param {any} value - The value to sanitize.
 * @returns {any} - The sanitized value.
 */
export const sanitizeForCsvInjection = (value) => {
  if (typeof value === 'string') {
    if (value.startsWith('=') || value.startsWith('+') || value.startsWith('-') || value.startsWith('@')) {
      return "'" + value;
    }
  }
  return value;
};

export const getExportFilename = (extension) => {
  const agora = new Date();
  const aaaa = agora.getFullYear();
  const mm = String(agora.getMonth() + 1).padStart(2, '0');
  const dd = String(agora.getDate()).padStart(2, '0');
  const hh = String(agora.getHours()).padStart(2, '0');
  const min = String(agora.getMinutes()).padStart(2, '0');
  return `Diagnostico_Mago_${aaaa}_${mm}_${dd}_${hh}h${min}.${extension}`;
};

export const exportToPdf = async (
  columns = [],
  data = [],
  orgName = '',
  filename
) => {
  const finalFilename = filename || getExportFilename('pdf');
  const cols = normalizeColumns(columns);
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4'
  });

  const logoPdf = await prepararLogoPdf(LOGO_MM.largura, LOGO_MM.altura);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  const agora = new Date();
  const dataFormatada = `${String(agora.getDate()).padStart(2, '0')}/${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()}`;
  const horaFormatada = `${String(agora.getHours()).padStart(2, '0')}h${String(agora.getMinutes()).padStart(2, '0')}`;

  let y = margin;

  const addHeaderAndFooter = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // --- HEADER ---
      desenharLogoPdf(doc, logoPdf, margin, 10);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.setFont('helvetica', 'normal');
      doc.text('Relatório de Diagnóstico - Sistema MAGO', pageWidth - margin, 15, { align: 'right' });
      if (orgName) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(orgName, pageWidth - margin, 20, { align: 'right' });
      }
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, 25, pageWidth - margin, 25);

      // --- FOOTER ---
      doc.setDrawColor(240, 240, 240);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(180, 180, 180);
      doc.text(`Exportado em: ${dataFormatada} às ${horaFormatada}`, margin, pageHeight - 8);
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    }
  };

  const checkPageBreak = (needed) => {
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      y = 35; // Start below header
      return true;
    }
    return false;
  };

  const dashboardRow = data.find(r => r.dashboard_mediaGeral !== undefined);
  const questionRows = data.filter(r => r.dashboard_mediaGeral === undefined);

  y = 35; // Initial Y

  questionRows.forEach((row, index) => {
    // Each question starts on a new page (except the first one on the first page)
    if (index > 0) {
      doc.addPage();
      y = 35;
    }

    // Pontuação e Nível de Maturidade
    const hasScore = cols.some(c => c.key === 'pontuacao');
    const hasMaturity = cols.some(c => c.key === 'nivel_maturidade');

    if (hasScore || hasMaturity) {
      const scoreValue = row.pontuacao || '0%';
      const maturityLabel = (row.nivel_maturidade || 'Inexpressivo').toUpperCase();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      const pctValue = parseFloat(scoreValue);
      if (pctValue >= 70) doc.setTextColor(34, 197, 94);
      else if (pctValue >= 40) doc.setTextColor(234, 179, 8);
      else if (pctValue >= 15) doc.setTextColor(249, 115, 22);
      else doc.setTextColor(239, 68, 68);

      doc.text(scoreValue, margin, y + 5);

      doc.setFontSize(9);
      doc.text(maturityLabel, margin, y + 10);

      const barX = margin + 50;
      const barWidth = contentWidth - 50;
      doc.setDrawColor(241, 245, 249);
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(barX, y + 3, barWidth, 1.5, 0.5, 0.5, 'F');

      doc.setFillColor(doc.getTextColor());
      doc.roundedRect(barX, y + 3, barWidth * (pctValue / 100), 1.5, 0.5, 0.5, 'F');

      y += 22;
    }

    // Código e Enunciado
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    const titleText = `${row.codigo ? row.codigo + ' - ' : ''}${row.enunciado || ''}`;
    const titleLines = doc.splitTextToSize(titleText, contentWidth);
    checkPageBreak(titleLines.length * 7);
    doc.text(titleLines, margin, y);
    y += (titleLines.length * 7) + 3;

    // Eixo e Responsável
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    let metaText = '';
    if (cols.some(c => c.key === 'eixo')) metaText += `Tema: ${row.eixo}   `;
    if (cols.some(c => c.key === 'responsavel')) metaText += `Articulador: ${row.responsavel}`;

    if (metaText) {
      doc.text(metaText, margin, y);
      y += 10;
    }

    // Resposta Principal
    if (cols.some(c => c.key === 'resposta_principal')) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 119, 182);
      doc.text('Resposta Principal', margin, y);
      y += 5;

      const respText = row.resposta_principal || 'Não respondida';
      const respLines = doc.splitTextToSize(respText, contentWidth - 10);
      checkPageBreak(respLines.length * 6 + 10);

      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, y, contentWidth, (respLines.length * 6) + 5, 1, 1, 'FD');

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      doc.text(respLines, margin + 5, y + 6);
      y += (respLines.length * 6) + 15;
    }

    // Itens de Verificação
    if (cols.some(c => c.key === 'itens_detalhados') && row.itens_detalhados) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 119, 182);
      doc.text('Itens de Verificação Selecionados', margin, y);
      y += 6;

      const items = row.itens_detalhados.split('\n');
      items.forEach(item => {
        const itemLines = doc.splitTextToSize(item, contentWidth - 15);
        checkPageBreak(itemLines.length * 6 + 5);

        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(margin, y, contentWidth, (itemLines.length * 6) + 4, 1, 1, 'D');

        // Determine status based on prefix [x] or [ ]
        let isChecked = true; // Default for backward compatibility
        let textToShow = item;

        if (item.trim().startsWith('[ ]')) {
          isChecked = false;
          textToShow = item.replace('[ ]', '').trim();
        } else if (item.trim().startsWith('[x]')) {
          isChecked = true;
          textToShow = item.replace('[x]', '').trim();
        }

        // Draw Indicator
        if (isChecked) {
          doc.setFillColor(34, 197, 94); // Green
          doc.circle(margin + 4, y + 4.5, 1.5, 'F');
        } else {
          doc.setDrawColor(203, 213, 225); // Gray border
          doc.setFillColor(255, 255, 255); // White fill
          doc.circle(margin + 4, y + 4.5, 1.5, 'FD');
        }

        const linesToPrint = doc.splitTextToSize(textToShow, contentWidth - 15);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(linesToPrint, margin + 10, y + 5.5);
        y += (itemLines.length * 6) + 6;
      });
      y += 6;
    }

    // Justificativa
    if (cols.some(c => c.key === 'justificativa') && row.justificativa) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 119, 182);
      doc.text('Justificativa e Evidências', margin, y);
      y += 6;

      const justLines = doc.splitTextToSize(row.justificativa, contentWidth - 10);
      checkPageBreak(justLines.length * 5 + 10);

      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, y, contentWidth, (justLines.length * 5) + 6, 2, 2, 'FD');

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(justLines, margin + 5, y + 6);
      y += (justLines.length * 5) + 15;
    }

    // Question Separator
    y += 5;
    doc.setDrawColor(241, 245, 249);
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;
  });

  // Dashboard Summary at the end
  if (dashboardRow) {
    if (y + 40 > pageHeight - 15) {
      doc.addPage();
      y = 35;
    } else {
      y += 10;
    }

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, y, contentWidth, 30, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text('Resumo do Dashboard', margin + 10, y + 10);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    let subY = y + 18;
    if (dashboardRow.dashboard_mediaGeral) {
      doc.text(`Média Geral: ${dashboardRow.dashboard_mediaGeral}`, margin + 10, subY);
      subY += 6;
    }
    if (dashboardRow.dashboard_taxaParticipacao) {
      doc.text(`Taxa de Participação: ${dashboardRow.dashboard_taxaParticipacao}`, margin + 10, subY);
    }
  }

  addHeaderAndFooter();
  doc.save(finalFilename);
};

export const exportToCsv = (columns = [], data = [], filename) => {
  const finalFilename = filename || getExportFilename('csv');
  const cols = normalizeColumns(columns);
  const rows = [];
  rows.push(cols.map(c => c.label).join(','));
  data.forEach(row => {
    const values = cols.map(c => {
      let val = row[c.key] ?? '';
      // Sentinel: Sanitize to prevent formula injection
      val = sanitizeForCsvInjection(val);

      if (typeof val === 'string' && val.includes(',')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    });
    rows.push(values.join(','));
  });
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', finalFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToXlsx = (columns = [], data = [], filename) => {
  const finalFilename = filename || getExportFilename('xlsx');
  const cols = normalizeColumns(columns);

  // Sentinel: Sanitize data for Formula Injection before creating sheet
  const sanitizedData = data.map(row => {
    const newRow = { ...row };
    Object.keys(newRow).forEach(key => {
      newRow[key] = sanitizeForCsvInjection(newRow[key]);
    });
    return newRow;
  });

  const worksheet = utils.json_to_sheet(sanitizedData, { header: cols.map(c => c.key) });
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Dados');
  writeFileXLSX(workbook, finalFilename);
};
